import React from "react";
import { useNavigate } from "react-router-dom";

import NavBar from "../components/common/nav_bar";
import MaintenanceBanner from "../components/common/maintenance_banner";
import ScrollToTopButton from "../components/common/scroll_to_top_button";
import Skeleton from "../components/common/skeleton";
import RetiredStrategyCard, {
    PARADIGM_ORDER,
    groupRetiredByParadigm,
    splitRetiredPnL,
} from "../components/paper/RetiredStrategyCard";

import { mergeRetiredWithMeta } from "../data/strategyMeta";
import { fetchPaperRetired } from "../services/paperApi";
import { requireLoginOrThrow } from "../utils/auth";
import { getNavCollapsed, setNavCollapsed } from "../utils/navState";

/**
 * PaperRetiredPage — /research/retired.
 *
 * 폐기(retired) 처리된 전략을 paradigm 별로 묶어 보여주는 카탈로그.
 * 폐기 상태/사유/최종통계는 백엔드(/api/paper/retired)에서 받아 strategyMeta
 * 설명과 머지한다 (DAS-126). 복원은 운영 Overview 탭에서만 가능.
 *
 * 정보:
 *   - 폐기 사유 (retiredReason)
 *   - 폐기 시각 (retiredAt)
 *   - 최종 누적 stats (finalStats)
 *   - 메커니즘/진입조건 (재참조용)
 */
class PaperRetiredPageInner extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            collapsed: getNavCollapsed(),
            retired: [],
            loading: true,
        };
    }

    componentDidMount() {
        // eslint-disable-next-line no-restricted-syntax -- componentDidMount only
        requireLoginOrThrow();
        fetchPaperRetired()
            .then((res) => {
                this.setState({
                    retired: mergeRetiredWithMeta(res.data),
                    loading: false,
                });
            })
            .catch(() => this.setState({ loading: false }));
    }

    onCollapse = (collapsed) => {
        setNavCollapsed(collapsed);
        this.setState({ collapsed });
    };

    onBack = () => {
        if (this.props.navigate) this.props.navigate("/research");
    };

    onDetail = (id) => {
        if (this.props.navigate) {
            this.props.navigate(`/research/strategies/${encodeURIComponent(id)}`);
        }
    };

    render() {
        const { collapsed, retired, loading } = this.state;
        const grouped = groupRetiredByParadigm(retired);
        const { lossSum, lossCount, integrationCount } = splitRetiredPnL(retired);

        return (
            <div className="min-h-screen w-full overflow-x-hidden bg-surface text-content sm:overflow-x-auto">
                <NavBar
                    activeMenu="research"
                    collapsed={collapsed}
                    onCollapse={this.onCollapse}
                />
                <main
                    className={`min-w-0 w-full pt-12 pb-16 sm:pt-0 sm:pb-0 transition-[padding-left] duration-200 ${
                        collapsed
                            ? "sm:pl-[88px] lg:pl-[96px]"
                            : "sm:pl-[252px] lg:pl-[260px]"
                    } px-4 py-4 sm:pr-8 sm:py-6 lg:pr-10`}
                    style={{ marginTop: "var(--maintenance-banner-h, 0px)" }}
                >
                    <MaintenanceBanner />
                    <button
                        type="button"
                        onClick={this.onBack}
                        aria-label="Research 목록으로 돌아가기"
                        className="inline-flex items-center gap-1 text-xs text-content-secondary hover:text-content mb-3 min-h-11 -ml-1 px-1"
                    >
                        ← Research 로
                    </button>

                    <header className="mb-6">
                        <h1 className="text-2xl font-bold text-content">폐기 전략 카탈로그</h1>
                        <p className="text-sm text-content-secondary mt-1">
                            paper trading 결과 또는 통합 정리로 운영 중단된 전략. 사유, 폐기 시점, 최종 누적 stats 를 참조용으로 보관한다.
                        </p>
                        <div className="bg-surface-card border border-surface-border rounded-lg p-3 mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                                <div className="text-xs text-content-muted">폐기 전략</div>
                                <div className="text-base font-semibold text-content mt-0.5">
                                    {retired.length} 건
                                    <span className="ml-1.5 text-xs text-content-muted font-normal">
                                        단독 {lossCount} / 통합 {integrationCount}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-content-muted">단독 paper 손실</div>
                                <div
                                    className={`text-base font-semibold tabular-nums mt-0.5 ${
                                        lossSum < 0 ? "text-loss" : lossSum > 0 ? "text-gain" : "text-content"
                                    }`}
                                    title={`${lossCount}건 axis 의 확정 손익 합계`}
                                >
                                    {lossSum.toLocaleString()} KRW
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-content-muted">통합 정리</div>
                                <div className="text-base font-semibold text-content mt-0.5">
                                    {integrationCount} 건
                                    <span className="ml-1.5 text-xs text-content-muted font-normal">
                                        trigger 중복
                                    </span>
                                </div>
                            </div>
                        </div>
                    </header>

                    {loading && retired.length === 0 ? (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                            {[0, 1, 2, 3].map((i) => (
                                <Skeleton key={i} variant="card" height="120px" />
                            ))}
                        </div>
                    ) : retired.length === 0 ? (
                        <section className="bg-surface-card border border-surface-border rounded-lg p-6 text-center text-sm text-content-secondary">
                            현재 폐기 처리된 전략이 없습니다.
                        </section>
                    ) : (
                        <section>
                            {PARADIGM_ORDER.concat(
                                Object.keys(grouped).filter((k) => !PARADIGM_ORDER.includes(k)),
                            ).map((paradigm) => {
                                const list = grouped[paradigm];
                                if (!list || list.length === 0) return null;
                                return (
                                    <div key={paradigm} className="mb-6">
                                        <div className="flex items-center mb-2">
                                            <span
                                                aria-hidden="true"
                                                className="inline-block w-1 h-3 bg-loss rounded-sm mr-2"
                                            />
                                            <div className="text-xs text-content-muted uppercase tracking-wide">
                                                {paradigm} ({list.length})
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                            {list.map((s) => (
                                                <RetiredStrategyCard
                                                    key={s.id}
                                                    strategy={s}
                                                    onClick={this.onDetail}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </section>
                    )}

                    <ScrollToTopButton />
                </main>
            </div>
        );
    }
}

export default function PaperRetiredPage() {
    const navigate = useNavigate();
    return <PaperRetiredPageInner navigate={navigate} />;
}
